import CloseIcon from "@mui/icons-material/Close";
import { Box, Button, Dialog, IconButton, Switch } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  selectAutoConnectToUnity,
  selectAutoSave,
  selectThemeMode,
  setAutoConnectToUnity,
  setAutoSave,
  setThemeMode,
  ThemeMode,
} from "../../app/slices/GlobalSlice";
import {
  setOnboardingReset,
  setShortcutsOverlayOpen,
} from "../../app/slices/OnboardingSlice";
import { selectIsSuperUser } from "../../app/slices/UserSlice";
import PiDropdown from "../../components/PiDropdown";
import PiSegmentedToggle from "../../components/PiSegmentedToggle";
import { useEditorLanguage } from "../i18n/useEditorLanguage";
import { useOnboardingStrategy } from "../onboarding/OnboardingHost";
import { useTourLauncher } from "../onboarding/useTourLauncher";
import ClearOnboardingDialog from "./ClearOnboardingDialog";
import SettingsRow from "./SettingsRow";
import SettingsSection from "./SettingsSection";
import "./SettingsModal.css";

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Editor preferences, grouped.
 *
 * Three things about this dialog are decisions rather than styling:
 *
 *  - It is a plain MUI Dialog, not PiDialog. PiDialog takes a fixed height and
 *    never forwards onClose, so Escape and backdrop clicks do nothing. A
 *    preferences dialog that traps you is worse than one that costs 20 lines
 *    more.
 *  - Rows disappear rather than going disabled. Auto connect is staff only,
 *    and the shortcuts overlay does not exist outside the editor, so on the
 *    dashboard that row would open nothing.
 *  - Nothing is applied on Done. Every control writes on change, which is what
 *    the footer hint says, and Done is only a way out.
 */
export default function SettingsModal(props: SettingsModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const themeMode = useSelector(selectThemeMode);
  const autoSave = useSelector(selectAutoSave);
  const autoConnectToUnity = useSelector(selectAutoConnectToUnity);
  const isSuperUser = useSelector(selectIsSuperUser);

  const language = useEditorLanguage();
  const strategy = useOnboardingStrategy();
  const { canStart, restartTour } = useTourLauncher();

  const [isClearOnboardingOpen, setIsClearOnboardingOpen] = useState(false);

  /**
   * The overlay is mounted by TrainingEditor, and the dashboard strategy has
   * no shortcuts at all, so this row is only honest inside an editor.
   */
  const hasShortcuts = strategy.shortcutGroups.length > 0;

  /**
   * piGrey is a borderless text button, which reads as plain text next to a
   * row of switches, so the border is what makes it an affordance. The height
   * matches the dropdown and the segmented control, and !w-full is needed
   * because only some variants set width themselves: without it the buttons in
   * one column come out at different widths.
   */
  const rowButtonClassName =
    "!w-full !h-[32px] !rounded-[6px] !py-0 !text-[13px]";
  const greyRowButtonClassName =
    rowButtonClassName + " !border !border-solid !border-creator-border";

  const onChangeTheme = (mode: ThemeMode) => {
    // A segmented control also fires on the segment that is already active.
    // Each dispatch queues a full PUT of the user settings, so drop the no-op.
    if (mode === themeMode) return;
    dispatch(setThemeMode(mode));
  };

  const onOpenShortcuts = () => {
    // Close first: the overlay is mounted by the editor, so it would otherwise
    // render behind this dialog.
    props.onClose();
    dispatch(setShortcutsOverlayOpen(true));
  };

  const onRestartTour = () => {
    props.onClose();
    restartTour();
  };

  const onConfirmClearOnboarding = () => {
    setIsClearOnboardingOpen(false);
    // Clearing puts the welcome choice back to unseen, and the onboarding host
    // greets the user as soon as it renders. Two stacked dialogs otherwise.
    props.onClose();
    dispatch(setOnboardingReset());
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        maxWidth={false}
        aria-labelledby="settings-dialog-title"
        data-testid="settings-dialog"
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(39, 45, 55, 0.45)" } },
        }}
        PaperProps={{
          sx: {
            width: 560,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 64px)",
            borderRadius: "10px",
            backgroundColor: "var(--pi-surface)",
            backgroundImage: "none",
            boxShadow: "0 16px 40px rgba(39, 45, 55, 0.18)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: "16px",
            padding: "22px 22px 0 22px",
          }}
        >
          <div className="flex flex-col gap-[2px] flex-1 min-w-0">
            <div
              id="settings-dialog-title"
              className="text-[22px] font-semibold leading-[30px] text-creator-text"
            >
              {t("settings")}
            </div>
            <div className="text-[12.5px] leading-[18px] text-creator-text-sub">
              {t("settings_ui.subtitle")}
            </div>
          </div>
          <IconButton
            aria-label="settings-close-button"
            data-testid="settings-close-button"
            onClick={props.onClose}
            size="small"
            className="hover:bg-creator-primary-washed"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "18px",
            overflowY: "auto",
            padding: "18px 22px",
          }}
        >
          <SettingsSection title={t("settings_ui.group.appearance")}>
            <SettingsRow
              label={t("settings_ui.theme.label")}
              testId="settings-theme"
              control={
                <PiSegmentedToggle<ThemeMode>
                  ariaLabel={t("settings_ui.theme.label") as string}
                  variant="square"
                  value={themeMode}
                  onChange={onChangeTheme}
                  options={[
                    {
                      value: "light",
                      label: t("settings_ui.theme.light"),
                      testId: "settings-theme-light",
                    },
                    {
                      value: "dark",
                      label: t("settings_ui.theme.dark"),
                      testId: "settings-theme-dark",
                    },
                  ]}
                />
              }
            />
            <SettingsRow
              label={t("settings_ui.language.label")}
              testId="settings-language"
              control={
                <div className="w-[150px] settingsmodal-dropdown">
                  <PiDropdown
                    // Without this the menu sizes itself and hangs past the
                    // trigger on both sides.
                    width="150px"
                    selectedText={language.current.full}
                    options={language.languages.map((item) => ({
                      text: item.full,
                    }))}
                    onClick={(index) =>
                      language.change(language.languages[index].short)
                    }
                  />
                </div>
              }
            />
          </SettingsSection>

          <SettingsSection title={t("settings_ui.group.editing")}>
            <SettingsRow
              label={t("autoSave")}
              testId="settings-autosave"
              control={
                <Switch
                  checked={autoSave}
                  onChange={() => dispatch(setAutoSave(!autoSave))}
                  color="primary"
                  data-testid="settings-autosave-switch"
                />
              }
            />
            {isSuperUser ? (
              <SettingsRow
                label={t("settings_ui.auto_connect.label")}
                description={t("settings_ui.auto_connect.description") as string}
                testId="settings-autoconnect"
                control={
                  <Switch
                    checked={autoConnectToUnity}
                    onChange={() =>
                      dispatch(setAutoConnectToUnity(!autoConnectToUnity))
                    }
                    color="primary"
                    data-testid="settings-autoconnect-switch"
                  />
                }
              />
            ) : null}
          </SettingsSection>

          <SettingsSection title={t("settings_ui.group.help")}>
            {hasShortcuts ? (
              <SettingsRow
                label={t("onboarding.help.shortcuts")}
                testId="settings-shortcuts-row"
                control={
                  <div className="w-[104px]">
                    <Button
                      variant="piGrey"
                      className={greyRowButtonClassName}
                      onClick={onOpenShortcuts}
                      data-testid="settings-shortcuts"
                    >
                      {t("settings_ui.shortcuts.action")}
                    </Button>
                  </div>
                }
              />
            ) : null}
            <SettingsRow
              label={t("settings_ui.tour.label")}
              testId="settings-tour-row"
              control={
                <div className="w-[104px]">
                  <Button
                    variant="piGrey"
                    className={greyRowButtonClassName}
                    disabled={!canStart}
                    onClick={onRestartTour}
                    data-testid="settings-restart-tour"
                  >
                    {t("settings_ui.tour.action")}
                  </Button>
                </div>
              }
            />
            <SettingsRow
              label={t("settings_ui.clear_onboarding.label")}
              description={t("settings_ui.clear_onboarding.description") as string}
              testId="settings-clear-onboarding-row"
              control={
                <div className="w-[104px]">
                  <Button
                    variant="piRedOutlined"
                    className={rowButtonClassName}
                    onClick={() => setIsClearOnboardingOpen(true)}
                    data-testid="settings-clear-onboarding"
                  >
                    {t("settings_ui.clear_onboarding.action")}
                  </Button>
                </div>
              }
            />
          </SettingsSection>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "0 22px 20px 22px",
          }}
        >
          {/* piBlue is width: 100%, so the box is what sizes it. */}
          <div className="w-[120px] shrink-0">
            <Button
              variant="piBlue"
              onClick={props.onClose}
              data-testid="settings-done"
            >
              {t("settings_ui.done")}
            </Button>
          </div>
        </Box>
      </Dialog>

      <ClearOnboardingDialog
        open={isClearOnboardingOpen}
        onClose={() => setIsClearOnboardingOpen(false)}
        onConfirm={onConfirmClearOnboarding}
      />
    </>
  );
}
